import React, { useMemo, useState } from 'react';
import {
  AlertTriangle, AlertCircle, Clock, Timer, CheckCircle2, PauseCircle,
  Users, Building2, Send, FileCheck, Activity,
  ChevronDown, ChevronUp, PenLine, Eye, Landmark, Snowflake, ArrowRight,
  FlameKindling, HardHat, ShieldCheck, TrendingDown, Sparkles, Flame,
  BarChart3,
} from 'lucide-react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../ui/hover-card';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';

/**
 * Comprehensive overview panel for the packs dashboards. Two modes:
 *   - tab='active'          → pre-lodgement pipeline
 *   - tab='post_lodgement'  → lodged files (SLA + MIR live view)
 *
 * Layout:
 *   1. Pulse hero (branded, headline + at-a-glance mini-stats)
 *   2. "Action Required" priority-ranked cards (owner + age + what to do)
 *   3. Big KPI grid — every metric brokers need to see at a glance
 *   4. Distribution stripe — where files are right now
 * All derived from the packs array — no extra network calls.
 */
export default function PackDashboardSummary({ tab, packs, onOpenPack, onFilterOverdue, onFilterMirs, onFilterStatus, currentBroker }) {
  const stats = useMemo(() => derive(packs, currentBroker), [packs, currentBroker]);
  if (packs.length === 0) return null;
  if (tab === 'post_lodgement') return <PostLodgementSummary stats={stats} onOpenPack={onOpenPack} onFilterOverdue={onFilterOverdue} onFilterMirs={onFilterMirs} onFilterStatus={onFilterStatus} />;
  if (tab === 'active') return <ActiveSummary stats={stats} onOpenPack={onOpenPack} onFilterStatus={onFilterStatus} />;
  return null;
}

// ─── Derivation ────────────────────────────────────────────────
const daysSince = (iso) => {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
};

function derive(packs, currentBroker) {
  const byStatus = packs.reduce((a, p) => { a[p.status] = (a[p.status] || 0) + 1; return a; }, {});
  const avgCompletion = packs.length ? Math.round(packs.reduce((s, p) => s + (p.completion || 0), 0) / packs.length) : 0;
  const mine = currentBroker ? packs.filter(p => p.broker === currentBroker) : [];

  const preLodgement = packs.filter(p => p.phase === 'pre_lodgement');
  const beingPrepared = preLodgement.filter(p => p.status === 'in_progress' || p.status === 'not_started');
  const forReview = preLodgement.filter(p => p.status === 'ready_for_review' || p.status === 'changes_required');
  const waitingInfo = preLodgement.filter(p => p.status === 'waiting_on_info');
  const readyToLodge = preLodgement.filter(p => p.completion === 100);

  const postLodgement = packs.filter(p => p.phase === 'post_lodgement');
  const withinSla = postLodgement.filter(p => p.sla_indicator === 'on_track');
  const dueToday = postLodgement.filter(p => p.sla_indicator === 'due_today');
  const overdue = postLodgement.filter(p => p.sla_indicator === 'overdue');
  const dueSoon = postLodgement.filter(p => p.days_remaining !== null && p.days_remaining !== undefined && p.days_remaining > 0 && p.days_remaining <= 2);
  const withOpenMirs = postLodgement.filter(p => (p.mir_counts?.outstanding || 0) > 0);
  const openMirsTotal = postLodgement.reduce((s, p) => s + (p.mir_counts?.outstanding || 0), 0);

  // Urgent MIRs — any MIR whose age is > 5 days AND still outstanding
  const urgentMirs = [];
  postLodgement.forEach(p => {
    (p.mirs || []).forEach(m => {
      if (m.completed) return;
      const age = daysSince(m.date_received || m.created_at);
      if (age !== null && age >= 5) urgentMirs.push({ pack: p, mir: m, age });
    });
  });

  const waitingClient = postLodgement.filter(p => p.status === 'waiting_on_client');
  const waitingDerwent = postLodgement.filter(p => p.status === 'waiting_on_derwent');
  const waitingThird = postLodgement.filter(p => p.status === 'waiting_on_third_party');
  const backWithLender = postLodgement.filter(p => p.status === 'back_with_lender' || p.status === 'all_mirs_satisfied' || p.status === 'lodged');
  const inApproval = postLodgement.filter(p => p.status === 'conditional_approval' || p.status === 'formal_approval');

  // Inactivity — files not updated in 3+ days
  const inactive = packs.filter(p => {
    const d = daysSince(p.last_updated_at);
    return d !== null && d >= 3 && p.status !== 'completed';
  });

  return {
    total: packs.length,
    mine,
    avgCompletion,
    byStatus,
    preLodgement, beingPrepared, forReview, waitingInfo, readyToLodge,
    postLodgement, withinSla, dueToday, dueSoon, overdue,
    withOpenMirs, openMirsTotal, urgentMirs,
    waitingClient, waitingDerwent, waitingThird, backWithLender, inApproval,
    inactive,
  };
}

// ─── Palette (all inline styles to defeat dark-mode inversion) ─
const TONE = {
  slate:   { bg: '#f8fafc', border: '#e2e8f0', ink: '#0f172a', accent: '#475569', mutedInk: '#64748b' },
  blue:    { bg: '#eff6ff', border: '#bfdbfe', ink: '#1e3a8a', accent: '#2563eb', mutedInk: '#1e40af' },
  indigo:  { bg: '#eef2ff', border: '#c7d2fe', ink: '#312e81', accent: '#4f46e5', mutedInk: '#3730a3' },
  amber:   { bg: '#fffbeb', border: '#fcd34d', ink: '#78350f', accent: '#d97706', mutedInk: '#92400e' },
  rose:    { bg: '#fff1f2', border: '#fda4af', ink: '#881337', accent: '#e11d48', mutedInk: '#9f1239' },
  emerald: { bg: '#ecfdf5', border: '#6ee7b7', ink: '#064e3b', accent: '#059669', mutedInk: '#047857' },
  purple:  { bg: '#faf5ff', border: '#d8b4fe', ink: '#581c87', accent: '#9333ea', mutedInk: '#6b21a8' },
  teal:    { bg: '#f0fdfa', border: '#5eead4', ink: '#134e4a', accent: '#0d9488', mutedInk: '#0f766e' },
  sky:     { bg: '#f0f9ff', border: '#7dd3fc', ink: '#0c4a6e', accent: '#0284c7', mutedInk: '#075985' },
  fuchsia: { bg: '#fdf4ff', border: '#f0abfc', ink: '#701a75', accent: '#c026d3', mutedInk: '#86198f' },
};

// ─── Pulse Hero — the very first thing you see ───────────────
// Light pastel aesthetic matching the "Today's Focus" reference:
// dark slate chip at top-left, date top-right, 4 pastel stat cards
// (peach / mint / lavender / sky) and a "Needs your attention" or
// "All files on track" section below.
function PulseHero({ isActive, stats }) {
  const needAction = isActive
    ? (stats.readyToLodge.length + stats.forReview.length + stats.waitingInfo.length)
    : (stats.overdue.length + stats.dueToday.length + stats.urgentMirs.length + stats.waitingDerwent.length);
  const total = isActive ? stats.preLodgement.length : stats.postLodgement.length;
  const attention = needAction > 0;
  const now = new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });

  // Compose the "attention rows" — soft-tinted list items surfacing what
  // needs the broker's eyes right now. Mirrors the reference's colour dots.
  const attentionRows = React.useMemo(() => {
    const rows = [];
    if (isActive) {
      if (stats.readyToLodge.length) rows.push({ tone: 'emerald', label: `${stats.readyToLodge.length} file${stats.readyToLodge.length === 1 ? '' : 's'} ready for lodgement — broker to press Lodge with Lender` });
      if (stats.forReview.length) {
        const changes = stats.byStatus.changes_required || 0;
        if (changes) rows.push({ tone: 'rose', label: `${changes} file${changes === 1 ? '' : 's'} with changes requested — support team needs updates` });
        const rfr = stats.forReview.length - changes;
        if (rfr > 0) rows.push({ tone: 'violet', label: `${rfr} file${rfr === 1 ? '' : 's'} awaiting credit team review` });
      }
      if (stats.waitingInfo.length) rows.push({ tone: 'amber', label: `${stats.waitingInfo.length} file${stats.waitingInfo.length === 1 ? '' : 's'} waiting on client — chase outstanding documents` });
      const inactive = stats.inactive.filter(p => p.phase === 'pre_lodgement').length;
      if (inactive) rows.push({ tone: 'slate', label: `${inactive} file${inactive === 1 ? '' : 's'} idle for 3+ days — check in or progress` });
    } else {
      if (stats.overdue.length) rows.push({ tone: 'rose', label: `${stats.overdue.length} lodged file${stats.overdue.length === 1 ? '' : 's'} past SLA — escalate to lender immediately` });
      if (stats.dueToday.length) rows.push({ tone: 'amber', label: `${stats.dueToday.length} lodged file${stats.dueToday.length === 1 ? '' : 's'} due today — expected response inbound` });
      if (stats.urgentMirs.length) rows.push({ tone: 'rose', label: `${stats.urgentMirs.length} urgent MIR${stats.urgentMirs.length === 1 ? '' : 's'} outstanding for 5+ days` });
      if (stats.waitingDerwent.length) rows.push({ tone: 'sky', label: `${stats.waitingDerwent.length} file${stats.waitingDerwent.length === 1 ? '' : 's'} waiting on Derwent — internal action required` });
    }
    return rows;
  }, [isActive, stats]);

  return (
    <div
      className="rounded-2xl mb-4 relative"
      data-testid="pulse-hero"
      style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 20px -12px rgba(15,23,42,0.08)',
        colorScheme: 'light',
      }}
    >
      <div className="p-5">
        {/* Top row: dark chip + date */}
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
            style={{ background: '#0f172a', color: '#ffffff' }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: attention ? '#f43f5e' : '#34d399',
                boxShadow: attention ? '0 0 0 3px rgba(244,63,94,0.20)' : '0 0 0 3px rgba(52,211,153,0.20)',
              }}
            />
            <span className="text-[10px] font-black tracking-[0.22em]">
              {isActive ? 'ACTIVE PIPELINE' : 'POST-LODGEMENT DESK'}
            </span>
          </span>
          <span className="text-[11.5px] font-semibold" style={{ color: '#64748b' }}>{now}</span>
        </div>

        {/* Pastel stat card grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {isActive ? (
            <>
              <MiniStat
                tone="peach"
                icon={Landmark}
                label={total === 1 ? 'In Progress' : 'Active Pipeline'}
                value={total}
                sub={`${stats.avgCompletion}% avg completion`}
                emphasize
              />
              <MiniStat
                tone="mint"
                icon={Send}
                label="Ready to Lodge"
                value={stats.readyToLodge.length}
                sub={stats.readyToLodge.length ? 'ready for lender' : 'none right now'}
              />
              <MiniStat
                tone="lavender"
                icon={Eye}
                label="For Review"
                value={stats.forReview.length}
                sub={stats.forReview.length ? 'credit team queue' : 'all clear'}
                pulse={(stats.byStatus.changes_required || 0) > 0}
              />
              <MiniStat
                tone="sky"
                icon={PauseCircle}
                label="Waiting on Client"
                value={stats.waitingInfo.length}
                sub={stats.waitingInfo.length ? 'chase outstanding info' : 'no chases open'}
              />
            </>
          ) : (
            <>
              <MiniStat
                tone="peach"
                icon={Landmark}
                label={total === 1 ? 'Lodged' : 'Lodged Files'}
                value={total}
                sub={`${stats.withinSla.length} on track · ${stats.inApproval.length} in approval`}
                emphasize
              />
              <MiniStat
                tone="mint"
                icon={CheckCircle2}
                label="Within SLA"
                value={stats.withinSla.length}
                sub={stats.withinSla.length ? 'no SLA risk' : '—'}
              />
              <MiniStat
                tone="lavender"
                icon={FileCheck}
                label="Open MIRs"
                value={stats.openMirsTotal}
                sub={stats.withOpenMirs.length ? `${stats.withOpenMirs.length} file${stats.withOpenMirs.length === 1 ? '' : 's'} with MIRs` : 'no MIRs open'}
                pulse={stats.urgentMirs.length > 0}
              />
              <MiniStat
                tone={stats.overdue.length ? 'rose' : 'sky'}
                icon={AlertTriangle}
                label="Overdue"
                value={stats.overdue.length}
                sub={stats.overdue.length ? 'escalate now' : 'nothing overdue'}
                pulse={stats.overdue.length > 0}
              />
            </>
          )}
        </div>

        {/* Attention / status section */}
        <div className="mt-5">
          <div className="text-[10.5px] font-black uppercase tracking-[0.16em] mb-2" style={{ color: attention ? '#334155' : '#334155' }}>
            {attention ? 'NEEDS YOUR ATTENTION' : 'ALL FILES ON TRACK'}
          </div>
          {attention ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {attentionRows.map((row, i) => (
                <AttentionRow key={i} tone={row.tone} label={row.label} />
              ))}
            </div>
          ) : (
            <AttentionRow
              tone="emerald"
              label={isActive
                ? 'Every active file is progressing normally. Great work team — enjoy the quiet.'
                : 'No SLAs breached and no urgent MIRs. Great work team — enjoy the quiet.'}
              variant="banner"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Pastel palette shared across MiniStat + AttentionRow + FactCard ──
const PASTEL = {
  peach:    { bg: '#ffedd5', ink: '#7c2d12', label: '#9a3412', sub: '#c2410c', chip: 'rgba(255,255,255,0.85)', dot: '#f97316' },
  mint:     { bg: '#d1fae5', ink: '#064e3b', label: '#047857', sub: '#059669', chip: 'rgba(255,255,255,0.85)', dot: '#10b981' },
  lavender: { bg: '#ede9fe', ink: '#4c1d95', label: '#6d28d9', sub: '#7c3aed', chip: 'rgba(255,255,255,0.85)', dot: '#8b5cf6' },
  sky:      { bg: '#dbeafe', ink: '#1e3a8a', label: '#1d4ed8', sub: '#2563eb', chip: 'rgba(255,255,255,0.85)', dot: '#3b82f6' },
  rose:     { bg: '#ffe4e6', ink: '#881337', label: '#9f1239', sub: '#be123c', chip: 'rgba(255,255,255,0.85)', dot: '#f43f5e' },
  amber:    { bg: '#fef3c7', ink: '#78350f', label: '#92400e', sub: '#b45309', chip: 'rgba(255,255,255,0.85)', dot: '#f59e0b' },
  emerald:  { bg: '#d1fae5', ink: '#064e3b', label: '#047857', sub: '#059669', chip: 'rgba(255,255,255,0.85)', dot: '#10b981' },
  violet:   { bg: '#ede9fe', ink: '#4c1d95', label: '#6d28d9', sub: '#7c3aed', chip: 'rgba(255,255,255,0.85)', dot: '#8b5cf6' },
  slate:    { bg: '#f1f5f9', ink: '#0f172a', label: '#475569', sub: '#64748b', chip: 'rgba(255,255,255,0.85)', dot: '#94a3b8' },
};

function MiniStat({ tone, icon: Icon, label, value, sub, pulse, emphasize }) {
  const c = PASTEL[tone] || PASTEL.slate;
  return (
    <div
      className="rounded-xl px-4 py-3.5 relative overflow-hidden"
      style={{ background: c.bg, colorScheme: 'light' }}
      data-testid={`ministat-${tone}`}
    >
      {pulse && (
        <div
          className="absolute top-3 right-3 w-2 h-2 rounded-full animate-pulse"
          style={{ background: c.dot, boxShadow: `0 0 0 3px ${c.bg}, 0 0 0 4px ${c.dot}40` }}
        />
      )}
      {/* Small icon+label pill (like reference) */}
      <div
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full"
        style={{ background: c.chip }}
      >
        <Icon className="w-3 h-3" style={{ color: c.label }} />
        <span className="text-[9px] font-black uppercase tracking-[0.14em]" style={{ color: c.label }}>{label}</span>
      </div>
      {/* Big value */}
      <div
        className={`${emphasize ? 'text-3xl md:text-[32px]' : 'text-3xl'} font-black tabular-nums leading-none mt-2.5`}
        style={{ color: c.ink }}
      >
        {value}
      </div>
      {/* Sub text */}
      {sub && (
        <div className="text-[11px] font-semibold mt-1 truncate" style={{ color: c.sub }}>{sub}</div>
      )}
    </div>
  );
}

function AttentionRow({ tone, label, variant = 'row' }) {
  const c = PASTEL[tone] || PASTEL.slate;
  return (
    <div
      className={`flex items-center gap-2.5 rounded-lg ${variant === 'banner' ? 'px-4 py-3' : 'px-3 py-2'}`}
      style={{ background: c.bg }}
      data-testid={`attention-row-${tone}`}
    >
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ background: c.dot, boxShadow: `0 0 0 3px rgba(255,255,255,0.5)` }}
      />
      <span className={`${variant === 'banner' ? 'text-[13px]' : 'text-[12px]'} font-semibold leading-snug`} style={{ color: c.ink }}>
        {label}
      </span>
    </div>
  );
}

// ─── Big KPI tile ─────────────────────────────────────────────
// Full-detail KPI card — now rendered INSIDE the popup content of a
// KpiPopupButton, not directly on the page. Same look as the original tile.
function KpiTile({ label, value, sub, icon: Icon, tone = 'slate', onClick, urgent, testid }) {
  const t = TONE[tone] || TONE.slate;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      data-testid={testid}
      className={`text-left rounded-xl border p-3 transition-all relative overflow-hidden ${onClick ? 'hover:shadow-md hover:-translate-y-0.5 cursor-pointer' : 'cursor-default'}`}
      style={{ background: t.bg, borderColor: t.border, colorScheme: 'light' }}
    >
      {urgent && (
        <div className="absolute top-0 right-0 w-2 h-2 rounded-full m-1.5 animate-pulse" style={{ background: t.accent }} />
      )}
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: t.accent, opacity: 0.85 }} />
      <div className="pl-2">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[9px] font-black uppercase tracking-[0.16em] leading-tight" style={{ color: t.mutedInk }}>{label}</div>
            <div className="text-2xl font-black tabular-nums leading-none mt-1.5" style={{ color: t.ink }}>{value}</div>
            {sub && <div className="text-[10px] font-semibold mt-1 truncate" style={{ color: t.accent }}>{sub}</div>}
          </div>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: t.accent }}>
            {Icon && <Icon className="w-3.5 h-3.5" style={{ color: '#ffffff' }} />}
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── Action Required rail ─────────────────────────────────────
function ActionRequiredPanel({ items, onOpenPack, testid, mode }) {
  // Collapsed by default so it doesn't dominate the page on load. Preference
  // is remembered per-mode (active vs post-lodgement).
  const storageKey = `lp-actionreq-open-${mode || 'default'}`;
  const [expanded, setExpanded] = useState(() => localStorage.getItem(storageKey) === '1');
  React.useEffect(() => {
    localStorage.setItem(storageKey, expanded ? '1' : '0');
  }, [storageKey, expanded]);
  const [maxShow, setMaxShow] = useState(6);
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border-2 mb-4 p-4 flex items-center gap-3" data-testid={testid}
        style={{ background: '#ecfdf5', borderColor: '#a7f3d0', colorScheme: 'light' }}>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#10b981' }}>
          <ShieldCheck className="w-5 h-5" style={{ color: '#ffffff' }} />
        </div>
        <div>
          <div className="text-[13px] font-black" style={{ color: '#064e3b' }}>Nothing on the priority list</div>
          <div className="text-[11px] font-semibold" style={{ color: '#047857' }}>
            {mode === 'post' ? 'Every lodged file is inside SLA with no urgent MIRs.' : 'Every active file is progressing normally.'}
          </div>
        </div>
      </div>
    );
  }
  const shown = expanded ? items.slice(0, maxShow) : [];
  const highCount = items.filter(i => i.priority === 'P1').length;
  const medCount = items.filter(i => i.priority === 'P2').length;
  const lowCount = items.filter(i => i.priority === 'P3').length;

  return (
    <div className="rounded-2xl border-2 shadow-lg overflow-hidden bg-white mb-4" data-testid={testid}
      style={{ borderColor: '#f1f5f9', colorScheme: 'light' }}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full px-4 py-3 flex items-center justify-between"
        style={{ background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)' }}
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#dc2626' }}>
            <FlameKindling className="w-4.5 h-4.5" style={{ color: '#ffffff' }} />
          </div>
          <div className="text-left">
            <div className="text-[14px] font-black flex items-center gap-2" style={{ color: '#ffffff' }}>
              Action Required · <span className="tabular-nums">{items.length} item{items.length === 1 ? '' : 's'}</span>
            </div>
            <div className="text-[10.5px] font-semibold mt-0.5 flex items-center gap-2 flex-wrap">
              {highCount > 0 && <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#f87171' }} /><span style={{ color: '#fecaca' }}>{highCount} P1 critical</span></span>}
              {medCount > 0 && <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#fbbf24' }} /><span style={{ color: '#fde68a' }}>{medCount} P2</span></span>}
              {lowCount > 0 && <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#94a3b8' }} /><span style={{ color: '#cbd5e1' }}>{lowCount} P3</span></span>}
            </div>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" style={{ color: '#cbd5e1' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#cbd5e1' }} />}
      </button>
      {expanded && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5 p-3" style={{ background: '#f8fafc' }}>
            {shown.map((it, idx) => (
              <ActionCard key={`${it.pack.id}-${idx}`} item={it} onOpen={() => onOpenPack?.(it.pack.id)} />
            ))}
          </div>
          {items.length > maxShow && (
            <button
              type="button"
              onClick={() => setMaxShow(items.length)}
              className="w-full py-2.5 text-[11px] font-black uppercase tracking-wider border-t hover:bg-slate-100"
              style={{ color: '#475569', borderColor: '#e2e8f0', background: '#ffffff' }}
              data-testid={`${testid}-showall`}
            >
              Show all {items.length} items <ArrowRight className="w-3 h-3 inline ml-1" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

function ActionCard({ item, onOpen }) {
  const t = TONE[item.tone] || TONE.slate;
  const Icon = item.icon;
  const priorityBg = item.priority === 'P1' ? '#dc2626' : item.priority === 'P2' ? '#d97706' : '#64748b';
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`action-item-${item.pack.id}`}
      className="text-left rounded-xl border-2 p-3 hover:shadow-md hover:-translate-y-0.5 transition-all"
      style={{ background: t.bg, borderColor: t.border, colorScheme: 'light' }}
    >
      {/* Prominent "waiting on X" status strip — the first thing you see */}
      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        <div className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 shrink-0" style={{ background: priorityBg }}>
          <span className="text-[9px] font-black tracking-wider" style={{ color: '#ffffff' }}>{item.priority}</span>
        </div>
        <div className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 shrink-0 shadow-sm" style={{ background: t.accent }}>
          <Icon className="w-3 h-3" style={{ color: '#ffffff' }} />
          <span className="text-[10.5px] font-black tracking-wider truncate max-w-[220px]" style={{ color: '#ffffff' }}>{item.badge}</span>
        </div>
        {item.ageDays !== undefined && item.ageDays !== null && item.ageDays > 0 && (
          <span className="text-[10px] font-black tabular-nums px-1.5 py-0.5 rounded-md" style={{ background: 'rgba(255,255,255,0.9)', color: t.mutedInk, border: `1px solid ${t.border}` }}>
            {item.ageDays}d outstanding
          </span>
        )}
      </div>

      {/* Client + broker line */}
      <div className="mb-2">
        <div className="text-[14px] font-black truncate" style={{ color: t.ink }}>{item.pack.client_name}</div>
        <div className="text-[10.5px] font-semibold mt-0.5 truncate" style={{ color: t.mutedInk }}>
          {item.pack.lender} · Broker {item.pack.broker}
          {item.pack.credit_team_member ? ` · Credit ${item.pack.credit_team_member}` : ''}
        </div>
      </div>

      {/* What to do */}
      <div className="rounded-md px-2 py-1.5 text-[11px] font-semibold mb-1.5" style={{ background: 'rgba(255,255,255,0.75)', color: t.ink }}>
        <b>What to do:</b> {item.what}
      </div>

      {/* Owner + extra */}
      <div className="flex items-center justify-between gap-2 text-[10px]" style={{ color: t.mutedInk }}>
        <span className="truncate"><b>Assigned to:</b> {item.owner || 'Unassigned'}</span>
        {item.extra && <span className="italic truncate ml-2">{item.extra}</span>}
      </div>
    </button>
  );
}

// ─── Distribution stripe ──────────────────────────────────────
function DistributionStripe({ segments, title }) {
  const total = segments.reduce((s, x) => s + x.count, 0);
  if (total === 0) return null;
  return (
    <div className="rounded-xl border overflow-hidden mb-3 bg-white" style={{ borderColor: '#e2e8f0' }} data-testid="distribution-stripe">
      <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: '#e2e8f0', background: '#f8fafc' }}>
        <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: '#0f172a' }}>{title}</span>
        <span className="text-[10px] font-bold tabular-nums" style={{ color: '#64748b' }}>{total} files</span>
      </div>
      <div className="flex h-7">
        {segments.filter(s => s.count > 0).map(s => (
          <div key={s.key} title={`${s.label}: ${s.count}`}
            className="flex items-center justify-center transition-all hover:opacity-90"
            style={{ width: `${(s.count / total) * 100}%`, background: s.tone, minWidth: '3%' }}>
            <span className="text-[10px] font-black tabular-nums" style={{ color: '#ffffff' }}>{s.count}</span>
          </div>
        ))}
      </div>
      <div className="px-3 py-2 flex flex-wrap gap-x-3 gap-y-1 text-[9.5px] font-bold" style={{ color: '#64748b' }}>
        {segments.filter(s => s.count > 0).map(s => (
          <span key={s.key} className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ background: s.tone }} />
            {s.label} <span className="tabular-nums" style={{ color: '#0f172a' }}>{s.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── KPI section wrapper (label + tile grid) ───────────────────
/**
 * KpiPopupButton — the collapsed "View XYZ" button that expands into a
 * grid of KpiTiles on hover OR click.
 *
 * When `urgent` is true (something overdue / SLA-breached), the button
 * flips to a red gradient with a subtle "jingle" shake animation and a
 * pulsing flame badge so the team notices without staring at the page.
 */
function KpiPopupButton({ label, subtitle, icon: Icon = BarChart3, urgent, urgentCount, children, testid, cols = 6 }) {
  const [open, setOpen] = useState(false);

  const buttonClasses = urgent
    ? 'text-white shadow-md animate-jingle'
    : 'shadow-sm hover:shadow-md text-slate-700';
  const buttonStyle = urgent
    ? { background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)', borderColor: '#991b1b' }
    : { background: '#ffffff', borderColor: '#e2e8f0' };

  return (
    <div className="mb-3" data-testid={testid}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            onMouseEnter={() => setOpen(true)}
            onClick={() => setOpen(v => !v)}
            className={`inline-flex items-center gap-2 rounded-lg border-2 pl-2 pr-3 py-1.5 transition-all ${buttonClasses}`}
            style={buttonStyle}
            data-urgent={urgent ? 'true' : 'false'}
          >
            <span
              className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
              style={{ background: urgent ? 'rgba(255,255,255,0.22)' : '#eef2ff' }}
            >
              {urgent
                ? <Flame className="w-3.5 h-3.5" style={{ color: '#ffffff' }} />
                : <Icon className="w-3.5 h-3.5" style={{ color: '#4f46e5' }} />}
            </span>
            <span className="flex flex-col items-start leading-tight">
              <span className="text-[11.5px] font-black tracking-tight">View {label}</span>
              {subtitle && (
                <span className={`text-[9px] font-semibold uppercase tracking-widest ${urgent ? 'text-white/85' : 'text-slate-400'}`}>
                  {subtitle}
                </span>
              )}
            </span>
            {urgent && (
              <span
                className="inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest animate-pulse"
                style={{ background: '#ffffff', color: '#b91c1c' }}
              >
                <AlertTriangle className="w-2.5 h-2.5" />
                {urgentCount} needs action
              </span>
            )}
            <ChevronDown className={`w-3.5 h-3.5 ml-0.5 transition-transform ${open ? 'rotate-180' : ''} ${urgent ? 'text-white/80' : 'text-slate-400'}`} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="start"
          onMouseLeave={() => setOpen(false)}
          className="p-4 shadow-2xl border-2"
          style={{ borderColor: urgent ? '#fda4af' : '#e2e8f0', background: '#ffffff', colorScheme: 'light', width: 'min(880px, 92vw)' }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-3.5 h-3.5" style={{ color: urgent ? '#dc2626' : '#4f46e5' }} />
            <div className="text-[12px] font-black uppercase tracking-[0.12em]" style={{ color: '#0f172a' }}>{label}</div>
            {subtitle && <div className="text-[10.5px] font-semibold text-slate-500">· {subtitle}</div>}
          </div>
          <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-${cols} gap-2.5`}>
            {children}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Legacy alias — kept in case any other file imports KpiSection
function KpiSection({ label, children }) {
  return <KpiPopupButton label={label}>{children}</KpiPopupButton>;
}

// ─── Active summary ───────────────────────────────────────────
function ActiveSummary({ stats, onOpenPack, onFilterStatus }) {
  // Priority-ranked action items
  const items = [];
  stats.readyToLodge.forEach(p => items.push({
    pack: p, priority: 'P1', tone: 'emerald', icon: Send,
    badge: 'READY FOR LODGEMENT',
    what: 'File is 100% complete — confirm details and lodge with the lender.',
    owner: p.broker,
    extra: 'Broker to press Lodge',
    ageDays: daysSince(p.last_updated_at),
  }));
  stats.forReview.forEach(p => items.push({
    pack: p,
    priority: p.status === 'changes_required' ? 'P1' : 'P2',
    tone: p.status === 'changes_required' ? 'rose' : 'indigo', icon: PenLine,
    badge: p.status === 'changes_required' ? 'SUPPORT TEAM ACTION REQUIRED' : 'CREDIT TEAM ACTION REQUIRED',
    what: p.status === 'changes_required'
      ? 'Support / credit team has flagged changes — broker to update the file.'
      : 'Credit team to review and approve before lodgement.',
    owner: p.credit_team_member || p.broker,
    ageDays: daysSince(p.last_updated_at),
  }));
  stats.waitingInfo.forEach(p => items.push({
    pack: p, priority: 'P2', tone: 'amber', icon: PauseCircle,
    badge: 'WAITING ON CLIENT',
    what: 'Chase the client for outstanding documents / information to unblock this file.',
    owner: p.broker,
    extra: 'Broker chasing client',
    ageDays: daysSince(p.last_updated_at),
  }));
  stats.inactive
    .filter(p => p.phase === 'pre_lodgement' && !items.some(i => i.pack.id === p.id))
    .forEach(p => items.push({
      pack: p, priority: 'P3', tone: 'slate', icon: Snowflake,
      badge: 'WAITING ON BROKER',
      what: 'File has been idle — broker to progress or update the status.',
      owner: p.broker,
      ageDays: daysSince(p.last_updated_at),
    }));
  items.sort((a, b) => (a.priority > b.priority ? 1 : a.priority < b.priority ? -1 : (b.ageDays || 0) - (a.ageDays || 0)));

  const segments = [
    { key: 'not_started',      label: 'Not started',   tone: '#94a3b8', count: stats.byStatus.not_started || 0 },
    { key: 'in_progress',      label: 'In progress',   tone: '#2563eb', count: stats.byStatus.in_progress || 0 },
    { key: 'waiting_on_info',  label: 'Waiting on info', tone: '#f59e0b', count: stats.byStatus.waiting_on_info || 0 },
    { key: 'ready_for_review', label: 'For review',    tone: '#6366f1', count: stats.byStatus.ready_for_review || 0 },
    { key: 'changes_required', label: 'Changes',       tone: '#e11d48', count: stats.byStatus.changes_required || 0 },
    { key: 'ready_to_submit',  label: 'Ready',         tone: '#10b981', count: stats.byStatus.ready_to_submit || 0 },
  ];

  return (
    <div className="mb-5" data-testid="active-summary">
      <PulseHero isActive stats={stats} />
      <ActionRequiredPanel items={items} onOpenPack={onOpenPack} testid="active-action-required" mode="active" />

      <KpiPopupButton
        label="Pipeline Overview"
        subtitle="Where each active file sits right now"
        testid="pipeline-overview-button"
        urgent={stats.readyToLodge.length > 0 || (stats.byStatus.changes_required || 0) > 0}
        urgentCount={stats.readyToLodge.length + (stats.byStatus.changes_required || 0)}
      >
        <KpiTile testid="kpi-total" label="Total active" value={stats.preLodgement.length} sub={`${stats.avgCompletion}% avg completion`} icon={Landmark} tone="slate" />
        <KpiTile testid="kpi-inprep" label="Being prepared" value={stats.beingPrepared.length}
          sub={`${(stats.byStatus.not_started || 0)} not started`} icon={PenLine} tone="blue"
          onClick={() => onFilterStatus?.('in_progress')} />
        <KpiTile testid="kpi-forreview" label="For review" value={stats.forReview.length}
          sub={(stats.byStatus.changes_required || 0) ? `${stats.byStatus.changes_required} changes needed` : 'awaiting'} icon={Eye}
          tone={stats.byStatus.changes_required ? 'rose' : 'indigo'} urgent={!!stats.byStatus.changes_required}
          onClick={() => onFilterStatus?.('ready_for_review')} />
        <KpiTile testid="kpi-waitinginfo" label="Waiting on info" value={stats.waitingInfo.length}
          sub={stats.waitingInfo.length ? 'chase to unblock' : 'all clear'} icon={PauseCircle}
          tone={stats.waitingInfo.length ? 'amber' : 'emerald'}
          onClick={() => onFilterStatus?.('waiting_on_info')} />
        <KpiTile testid="kpi-ready" label="Ready to lodge" value={stats.readyToLodge.length}
          sub={stats.readyToLodge.length ? 'lodge now' : '—'} icon={Send} tone="emerald"
          urgent={stats.readyToLodge.length > 0} />
        <KpiTile testid="kpi-inactive" label="Inactive 3d+" value={stats.inactive.filter(p => p.phase === 'pre_lodgement').length}
          sub={stats.inactive.length ? 'chase' : 'all fresh'} icon={Snowflake}
          tone={stats.inactive.length ? 'purple' : 'emerald'} />
      </KpiPopupButton>

      <DistributionStripe segments={segments} title="Pipeline stage · where each file is" />
    </div>
  );
}

// ─── Post-Lodgement summary ───────────────────────────────────
function PostLodgementSummary({ stats, onOpenPack, onFilterOverdue, onFilterMirs, onFilterStatus }) {
  const items = [];
  stats.overdue.forEach(p => items.push({
    pack: p, priority: 'P1', tone: 'rose', icon: AlertTriangle,
    badge: `${Math.abs(p.days_remaining)}D OVERDUE`,
    what: `Chase the lender — expected response was ${p.expected_response_date}`,
    owner: p.credit_team_member || p.broker,
    ageDays: Math.abs(p.days_remaining),
    extra: p.expected_response_date,
  }));
  stats.dueToday.forEach(p => items.push({
    pack: p, priority: 'P1', tone: 'amber', icon: Clock,
    badge: 'DUE TODAY',
    what: 'Chase the lender before end of day',
    owner: p.credit_team_member || p.broker,
    ageDays: 0,
  }));
  stats.dueSoon.forEach(p => items.push({
    pack: p, priority: 'P2', tone: 'amber', icon: Timer,
    badge: `DUE IN ${p.days_remaining}D`,
    what: 'Line up the follow-up call and be ready to chase',
    owner: p.credit_team_member || p.broker,
    ageDays: -p.days_remaining,
  }));
  stats.urgentMirs.forEach(({ pack, mir, age }) => items.push({
    pack, priority: 'P1', tone: 'amber', icon: FileCheck,
    badge: `MIR ${age}D OLD`,
    what: (mir.description || '(unnamed MIR)').slice(0, 90),
    owner: mir.person_responsible || pack.broker,
    ageDays: age,
    extra: mir.waiting_on ? `Waiting on ${mir.waiting_on}` : undefined,
  }));
  stats.waitingDerwent.forEach(p => items.push({
    pack: p, priority: 'P2', tone: 'blue', icon: HardHat,
    badge: 'DERWENT ACTION REQUIRED',
    what: 'Support / credit team needs to complete an internal action.',
    owner: p.credit_team_member || p.broker,
    ageDays: daysSince(p.last_updated_at),
  }));
  stats.waitingClient.forEach(p => items.push({
    pack: p, priority: 'P2', tone: 'amber', icon: Users,
    badge: 'WAITING ON CLIENT',
    what: 'Chase the client for outstanding docs / information.',
    owner: p.broker,
    ageDays: daysSince(p.last_updated_at),
  }));
  stats.waitingThird.forEach(p => items.push({
    pack: p, priority: 'P2', tone: 'purple', icon: Building2,
    badge: 'WAITING ON 3RD PARTY',
    what: 'Chase the third party (accountant, employer, agent, …).',
    owner: p.broker,
    ageDays: daysSince(p.last_updated_at),
  }));
  stats.inactive
    .filter(p => p.phase === 'post_lodgement' && !items.some(i => i.pack.id === p.id))
    .forEach(p => items.push({
      pack: p, priority: 'P3', tone: 'slate', icon: Snowflake,
      badge: 'INACTIVE · CHECK FILE',
      what: 'No update in 3+ days — check the file and update the status.',
      owner: p.credit_team_member || p.broker,
      ageDays: daysSince(p.last_updated_at),
    }));
  items.sort((a, b) => (a.priority > b.priority ? 1 : a.priority < b.priority ? -1 : (b.ageDays || 0) - (a.ageDays || 0)));

  const waitingSegments = [
    { key: 'lodged',            label: 'Fresh lodged',       tone: '#4f46e5', count: (stats.byStatus.lodged || 0) },
    { key: 'mir_outstanding',   label: 'MIRs open',          tone: '#d97706', count: (stats.byStatus.mir_outstanding || 0) },
    { key: 'waiting_on_client', label: 'On client',          tone: '#f59e0b', count: stats.waitingClient.length },
    { key: 'waiting_on_derwent', label: 'On Derwent',        tone: '#2563eb', count: stats.waitingDerwent.length },
    { key: 'waiting_on_third_party', label: 'On third party', tone: '#9333ea', count: stats.waitingThird.length },
    { key: 'all_mirs_satisfied', label: 'Back with lender',  tone: '#0d9488', count: stats.backWithLender.length },
    { key: 'conditional_approval', label: 'Cond. approval',  tone: '#10b981', count: (stats.byStatus.conditional_approval || 0) },
    { key: 'formal_approval',   label: 'Formal approval',   tone: '#059669', count: (stats.byStatus.formal_approval || 0) },
  ];

  return (
    <div className="mb-5" data-testid="post-lodgement-summary">
      <PulseHero isActive={false} stats={stats} />
      <ActionRequiredPanel items={items} onOpenPack={onOpenPack} testid="post-action-required" mode="post" />

      <div className="flex flex-wrap gap-2 mb-3">
        <KpiPopupButton
          label="SLA & Urgency"
          subtitle="Lender turnaround & SLA breach live"
          testid="sla-urgency-button"
          icon={Timer}
          urgent={stats.overdue.length > 0 || stats.dueToday.length > 0 || stats.urgentMirs.length > 0}
          urgentCount={stats.overdue.length + stats.dueToday.length + stats.urgentMirs.length}
        >
          <KpiTile testid="kpi-lodged" label="Total lodged" value={stats.postLodgement.length} sub={`${stats.mine.length} yours`} icon={Send} tone="indigo" />
          <KpiTile testid="kpi-withinsla" label="Within SLA" value={stats.withinSla.length} sub="on track" icon={CheckCircle2} tone="emerald" />
          <KpiTile testid="kpi-duetoday" label="Due today" value={stats.dueToday.length}
            sub={stats.dueToday.length ? 'chase today' : '—'} icon={Clock}
            tone={stats.dueToday.length ? 'amber' : 'slate'} urgent={stats.dueToday.length > 0} />
          <KpiTile testid="kpi-overdue" label="Overdue" value={stats.overdue.length}
            sub={stats.overdue.length ? 'escalate' : 'all clear'} icon={AlertTriangle}
            tone={stats.overdue.length ? 'rose' : 'emerald'} urgent={stats.overdue.length > 0}
            onClick={stats.overdue.length ? onFilterOverdue : undefined} />
          <KpiTile testid="kpi-openmirs" label="Open MIRs" value={stats.openMirsTotal}
            sub={`${stats.withOpenMirs.length} file${stats.withOpenMirs.length === 1 ? '' : 's'}`} icon={FileCheck}
            tone={stats.openMirsTotal ? 'amber' : 'emerald'} urgent={stats.openMirsTotal > 0}
            onClick={stats.openMirsTotal ? onFilterMirs : undefined} />
          <KpiTile testid="kpi-urgentmirs" label="Urgent MIRs (5d+)" value={stats.urgentMirs.length}
            sub={stats.urgentMirs.length ? 'chase now' : 'no aged'} icon={AlertCircle}
            tone={stats.urgentMirs.length ? 'rose' : 'slate'} urgent={stats.urgentMirs.length > 0} />
        </KpiPopupButton>

        <KpiPopupButton
          label="Who Has the Ball"
          subtitle="Who's holding up each file"
          testid="who-has-ball-button"
          icon={Users}
          urgent={stats.waitingDerwent.length > 0}
          urgentCount={stats.waitingDerwent.length}
        >
          <KpiTile testid="kpi-waitingclient" label="On client" value={stats.waitingClient.length} icon={Users}
            sub="chase client" tone={stats.waitingClient.length ? 'amber' : 'slate'}
            onClick={() => onFilterStatus?.('waiting_on_client')} />
          <KpiTile testid="kpi-waitingderwent" label="On Derwent" value={stats.waitingDerwent.length} icon={HardHat}
            sub="internal action" tone={stats.waitingDerwent.length ? 'blue' : 'slate'}
            urgent={stats.waitingDerwent.length > 0}
            onClick={() => onFilterStatus?.('waiting_on_derwent')} />
          <KpiTile testid="kpi-waitingthird" label="On 3rd party" value={stats.waitingThird.length} icon={Building2}
            sub="external chase" tone={stats.waitingThird.length ? 'purple' : 'slate'}
            onClick={() => onFilterStatus?.('waiting_on_third_party')} />
          <KpiTile testid="kpi-backlender" label="Back with lender" value={stats.backWithLender.length} icon={Landmark}
            sub="assessor next" tone="teal" />
          <KpiTile testid="kpi-approval" label="In approval" value={stats.inApproval.length} icon={CheckCircle2}
            sub={`Cond ${stats.byStatus.conditional_approval || 0} · Form ${stats.byStatus.formal_approval || 0}`}
            tone="emerald" />
          <KpiTile testid="kpi-inactive" label="Inactive 3d+" value={stats.inactive.filter(p => p.phase === 'post_lodgement').length}
            sub={stats.inactive.length ? 'chase' : 'fresh'} icon={Snowflake}
            tone={stats.inactive.length ? 'slate' : 'emerald'} />
        </KpiPopupButton>
      </div>

      <DistributionStripe segments={waitingSegments} title="Where each lodged file is right now" />
    </div>
  );
}
